/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 *
 * Created by Justin K Kazmierczak.
 */
var _ = require("../../../uam/module.js")(module.exports, {
    namespace: "email.text",
    title: "Email Text",
    description: "A body paragraph row for platform emails.",
    supportServer: true,
    control: false,
    foruseIn: ["email"],
    fields: {
        "content": {
            title: "Content",
            type: "string",
            description: "The paragraph text to display.",
            required: true
        }
    }
});
var $ = require("../../../uam/scafolding.js");

exports.render = async function (options) {
    var { html } = await exports.renderEmail(options);
    return [{
        n: "ua.element.serverNotSupported",
        inner: {
            n: "json",
            type: "ua/interface",
            by: "email.text",
            i: JSON.stringify({
                n: "html",
                html: `<div style="max-width:480px;margin:0 auto;font-family:Arial,Helvetica,sans-serif;">${html}</div>`
            })
        }
    }];
};

/**
 * Converts a paragraph's markup into a clean text/plain equivalent.
 *
 * The text/plain alternative previously received `content` VERBATIM, so any
 * markup an author wrote (`<strong>`, `<em>`, `<a href>`, `<br>`) arrived in the
 * recipient's plain-text client as literal tags. This affects every tenant,
 * because every platform email body row goes through `email.text`.
 *
 * Deliberately dependency-free and conservative:
 *  - block/line breaks (`<br>`, `</p>`, `</div>`, `</li>`, `</tr>`) become newlines
 *  - a list item gains a leading "- " so bullets survive
 *  - `<a href="URL">label</a>` becomes `label (URL)` so the link is not lost
 *  - every remaining tag is dropped, then the handful of entities that HTML
 *    escaping actually produces are decoded (&amp; last, so "&amp;lt;" is safe)
 *  - runs of whitespace collapse, but blank lines between paragraphs survive
 *
 * Only the text/plain part uses this. The HTML part is untouched.
 * @param {*} value The paragraph content, possibly containing markup.
 * @returns {string} The plain-text equivalent.
 */
function toPlainText(value) {

    if (value === null || value === undefined) return "";

    var text = String(value);

    //nothing that looks like a tag or an entity? leave the author's text alone.
    if (text.indexOf("<") === -1 && text.indexOf("&") === -1) return text.trim();

    //keep the destination of a link, which stripping tags would otherwise lose.
    text = text.replace(/<a\b[^>]*\bhref\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))[^>]*>([\s\S]*?)<\/a\s*>/gi,
        function (match, raw, dq, sq, bare, label) {
            var href = (dq !== undefined ? dq : (sq !== undefined ? sq : (bare || ""))).trim();
            var inner = toPlainText(label).trim();

            if (!href) return inner;
            if (!inner) return href;
            if (inner === href) return href;
            return `${inner} (${href})`;
        });

    //drop anything whose text content should not be read out loud.
    text = text.replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, "");

    //bullets, before the generic tag strip removes the marker.
    text = text.replace(/<li\b[^>]*>/gi, "\n- ");

    //structural breaks become real newlines.
    text = text.replace(/<br\s*\/?>/gi, "\n");
    //`li` is absent on purpose — `<li>` already opened the line above, so closing
    //it too would put a blank line between every bullet.
    text = text.replace(/<\/(p|div|tr|h[1-6]|ul|ol|blockquote)\s*>/gi, "\n");
    text = text.replace(/<(p|div|tr|h[1-6]|ul|ol|blockquote)\b[^>]*>/gi, "\n");

    //everything else that is a tag goes away.
    text = text.replace(/<[^>]*>/g, "");

    //decode the entities HTML escaping produces. &amp; MUST be last.
    text = text.replace(/&nbsp;/gi, " ")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&quot;/gi, "\"")
        .replace(/&#0?39;|&apos;/gi, "'")
        .replace(/&amp;/gi, "&");

    //tidy: no trailing spaces, no runs of 3+ newlines, no leading/trailing blank.
    text = text.replace(/\r\n?/g, "\n")
        .replace(/[ \t]+/g, " ")
        .replace(/ *\n */g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();

    return text;
} exports.toPlainText = toPlainText;

exports.renderEmail = async function (options) {
    var content = (options && options.content) ? options.content : "";
    var cfg = $.config || {};
    var text = (cfg.my && cfg.my.app && cfg.my.app.colors && cfg.my.app.colors.text) || "#000000";
    // Body font: config-driven, neutral Arial fallback (NEVER a serif default).
    var bodyFont = (cfg.my && cfg.my.app && cfg.my.app.fonts && cfg.my.app.fonts.body) || "Arial,Helvetica,sans-serif";

    var html = `<tr>
  <td style="padding:8px 20px;font-family:${bodyFont};font-size:16px;color:${text};line-height:1.6;">
    <p style="margin:0;padding:0;">${content}</p>
  </td>
</tr>`;

    //the HTML part keeps `content` verbatim (above); only text/plain is converted.
    var plainText = toPlainText(content);

    return { html, text: plainText };
};
